import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import PhotoGallery from './PhotoGallery';
import ScrimmageStatsSummarySection from './ScrimmageStatsSummarySection';
import './PublicMatchSummary.css';

const PublicMatchSummary = () => {
  const { token } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPublicSummary(token);
      setSummary(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleShareViaWhatsApp = () => {
    if (!summary) return;

    const isPractice = !!summary.practiceId;
    const opponentName = isPractice ? 'Red Team vs Blue Team' : summary.opponentName;

    const shareUrl = api.shareSummaryViaWhatsApp(
      token,
      summary.matchTitle || summary.practiceTitle || 'Match Summary',
      summary.scoreHome,
      summary.scoreAway,
      opponentName,
      summary.manOfMatchPlayerName,
      summary.manOfMatchReason,
      summary.photoUrls,
      summary.nextMatchDate,
      summary.nextMatchTitle,
      summary.scrimmageStats
    );
    
    window.open(shareUrl, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading">Loading summary...</div>;
  }

  if (error) {
    return (
      <div className="card">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="card">
        <div className="alert alert-warning">Summary not found</div>
      </div>
    );
  }

  const title = summary.matchTitle || summary.practiceTitle;
  const date = summary.matchDate || summary.practiceDate;

  return (
    <div className="public-summary">
      <div className="card">
        <div className="summary-header">
          <h1>{title}</h1>
          <p className="club-name">{summary.clubName}</p>
        </div>

        <div className="summary-info">
          <div className="info-item">
            <strong>Date:</strong> {formatDate(date)}
          </div>
          {summary.location && (
            <div className="info-item">
              <strong>Location:</strong> {summary.location}
            </div>
          )}
        </div>

        {(summary.scoreHome !== null && summary.scoreAway !== null) && (
          <div className="score-section">
            <h2>Final Score</h2>
            <div className="score-display">
              <div className="score-team">
                <span className="team-name">
                  {summary.practiceId ? '🔴 Red Team' : summary.clubName}
                </span>
                <span className="score-value">{summary.scoreHome}</span>
              </div>
              <span className="score-separator">-</span>
              <div className="score-team">
                <span className="team-name">
                  {summary.practiceId ? '🔵 Blue Team' : (summary.opponentName || 'Opponent')}
                </span>
                <span className="score-value">{summary.scoreAway}</span>
              </div>
            </div>
          </div>
        )}

        {summary.manOfMatchPlayerName && (
          <div className="man-of-match-section">
            <h2>⭐ Man of the Match</h2>
            <p className="man-of-match-name">{summary.manOfMatchPlayerName}</p>
            {summary.manOfMatchReason && (
              <p className="man-of-match-reason" style={{ marginTop: '0.5rem', fontStyle: 'italic', color: '#666' }}>
                "{summary.manOfMatchReason}"
              </p>
            )}
          </div>
        )}

        {summary.scrimmageStats && (
          <ScrimmageStatsSummarySection scrimmageStats={summary.scrimmageStats} />
        )}

        {summary.photoUrls && summary.photoUrls.length > 0 && (
          <div className="photos-section">
            <h2>Match Photos</h2>
            <PhotoGallery photos={summary.photoUrls} />
          </div>
        )}

        {summary.nextMatchId && (
          <div className="next-match-section">
            <h2>Next Match</h2>
            <div className="next-match-info">
              {summary.nextMatchTitle && (
                <p><strong>{summary.nextMatchTitle}</strong></p>
              )}
              {summary.nextMatchDate && (
                <p>{formatDate(summary.nextMatchDate)}</p>
              )}
            </div>
          </div>
        )}

        <div className="share-section">
          <button
            className="btn btn-primary btn-whatsapp"
            onClick={handleShareViaWhatsApp}
          >
            📱 Share on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicMatchSummary;

